/**
 * DateChangeRequestCard — F-S2 (leadership dashboard).
 *
 * One pending date change request. Shows the project (link), the field, current → requested date,
 * the reason, and requester + time. Leadership can Approve (applies the date server-side) or Reject
 * (reveals an inline reason input — reason required). On resolve the parent list refetches and the
 * card disappears.
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowRight, CalendarClock, Check, Loader2, X } from 'lucide-react';

import type { DateChangeRequest } from '@/types';
import { useResolveDateChangeRequest } from '@/hooks/useDateChangeRequests';
import { dateFieldLabel, formatDateValue, timeAgo } from '@/lib/date-change';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';

export interface DateChangeRequestCardProps {
  request: DateChangeRequest;
}

export function DateChangeRequestCard({ request }: DateChangeRequestCardProps): JSX.Element {
  const resolve = useResolveDateChangeRequest();
  const [rejecting, setRejecting] = useState(false);
  const [rejectReason, setRejectReason] = useState('');

  const projectKey = request.jira_key ?? request.project_id;
  const projectName = request.project_title ?? projectKey;

  const handleApprove = (): void => {
    resolve.mutate(
      { request, input: { action: 'approve' } },
      {
        onSuccess: () => toast.success('Date change approved'),
        onError: () => toast.error('Failed to approve request'),
      }
    );
  };

  const handleReject = (): void => {
    const reason = rejectReason.trim();
    if (!reason) {
      toast.error('A rejection reason is required.');
      return;
    }
    resolve.mutate(
      { request, input: { action: 'reject', rejection_reason: reason } },
      {
        onSuccess: () => {
          toast.success('Date change rejected');
          setRejecting(false);
          setRejectReason('');
        },
        onError: () => toast.error('Failed to reject request'),
      }
    );
  };

  return (
    <Card className="border-amber-300/60 dark:border-amber-500/30">
      <CardContent className="space-y-3 p-5">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <Link
              to={`/projects/${projectKey}`}
              className="truncate font-semibold text-text-primary underline-offset-2 hover:underline"
            >
              {projectName}
            </Link>
            <div className="mt-0.5 flex items-center gap-2">
              <Badge variant="info" className="gap-1">
                <CalendarClock className="h-3 w-3" aria-hidden="true" />
                {dateFieldLabel(request.date_field)}
              </Badge>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2 text-sm">
          <span className="text-text-secondary">{formatDateValue(request.current_value)}</span>
          <ArrowRight className="h-3.5 w-3.5 text-text-secondary" aria-hidden="true" />
          <span className="font-semibold text-text-primary">
            {formatDateValue(request.requested_value)}
          </span>
        </div>

        <div className="rounded-md border bg-subtle-bg/40 p-3 text-sm text-text-primary">
          {request.reason}
        </div>

        <p className="text-xs text-text-secondary">
          Requested by {request.requested_by} • {timeAgo(request.requested_at)}
        </p>

        {rejecting ? (
          <div className="space-y-2 pt-1">
            <Label htmlFor={`reject-reason-${request.id}`}>Rejection reason *</Label>
            <Textarea
              id={`reject-reason-${request.id}`}
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Explain why this request is being rejected…"
              rows={3}
            />
            <div className="flex items-center gap-2">
              <Button
                variant="danger"
                size="sm"
                onClick={handleReject}
                disabled={resolve.isPending || !rejectReason.trim()}
              >
                {resolve.isPending && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
                Confirm reject
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setRejecting(false);
                  setRejectReason('');
                }}
                disabled={resolve.isPending}
              >
                Cancel
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-2 pt-1">
            <Button
              size="sm"
              className="flex-1 bg-emerald-600 text-white hover:bg-emerald-700"
              onClick={handleApprove}
              disabled={resolve.isPending}
            >
              {resolve.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
              ) : (
                <Check className="h-4 w-4" aria-hidden="true" />
              )}
              Approve
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="flex-1 border-red-300 text-red-700 hover:bg-red-50 dark:border-red-500/40 dark:text-red-400 dark:hover:bg-red-500/10"
              onClick={() => setRejecting(true)}
              disabled={resolve.isPending}
            >
              <X className="h-4 w-4" aria-hidden="true" />
              Reject
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default DateChangeRequestCard;
